import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom'
import logo from '../../../assets/images/logo.svg'
import menuIcon from '../../../assets/images/menu-icon.svg'
import downloadIcon from '../../../assets/images/download-icon.svg'
import logoutIcon from '../../../assets/images/buttons/LOG-OUT-icon.svg'
import sidebarItems, { SidebarItemTitle } from './const'
import IFrame from '@uiw/react-iframe'
import apiServices from '../../../libs/services'
import { SideBarItem } from '../../../models/global.types'
import { setInstitution } from '../../../redux/slices/institutionSlice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux/store'
import { ReturnButton } from '../../../components/molecules'
import { Text, SkipLink } from '../../../components/atoms'
import { useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../../enums/KeyboardControls'
import { KeyboardControl, ControlHighlight } from '../../../components/molecules/KeyboardControls'
import { useBackgroundMusic } from '../../../contexts/BackgroundMusicContext'
import { getBgmUrl } from '../../../utils/audio'
import { BgmTrack } from '../../../enums/enums'
import { setCurrentAttempt } from '../../../redux/slices/quizSlice'
import PageTitle from '../../../components/atoms/PageTitle/PageTitle'
import { pageTitle as accountDetailsTitle } from '../../UserDashboard/settings/accountDetails'
import { hasUrlFeatureFlagTrain } from '../../../components/molecules/URLFeatureFlag/URLFeatureFlag'
import { downloadCertificate } from '../../Completed/Completed'
import { useAuth } from '../../../hooks/useAuth'
import { useAuthContext } from '../../../contexts/AuthContext'
import AccountDetailsFooter from '../../../components/userAdmin/AccountDetailsFooter'
import ProgressiveImage from '../../../components/molecules/ProgressiveImage/ProgressiveImage'

const ARIA_LABEL_MAPPINGS: Record<string, string> = {
  'Testing for Fentanyl': 'Testing for Fentanyl Instructions Tab',
  'Good Samaritan Laws': 'Good Samaritan Laws Database Tab',
  'Your Policies': 'Your Policies Tab'
}

// Helper function to get the aria-label based on the title
const getAriaLabel = (title: string): string => {
  return ARIA_LABEL_MAPPINGS[title] !== undefined ? ARIA_LABEL_MAPPINGS[title] : `${title} Tab` // Default fallback for any future items
}

export enum FocusableElement {
  ImageLogo = 'image-logo',
  ButtonAccountDetails = 'button-account-details',
  IframeExternalContent = 'iframe-external-content',
  ButtonLogout = 'button-logout'
}

const idSidebarLinkCertificate = 'side-bar-menu-download-certificate'

interface PageTitleState {
  title: string
  addScreenReaderH1: boolean
}

const Dashboard: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { userProfile, setUserProfile } = useAuth()
  const { signout } = useAuthContext()
  const { pageType } = useParams()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const { currentAttempt } = useSelector((state: RootState) => state.quiz)
  const [contentUrl, setContentUrl] = useState<string>('')
  const [sidebarLinks, setSidebarLinks] = useState<SideBarItem[]>([])
  const [currentPageTitle, setCurrentPageTitle] = useState<PageTitleState>({
    title: 'Player Pack',
    addScreenReaderH1: true
  })
  const CAMPUS_SLUG = 'your-campus-policies'
  const { institution } = useSelector((state: RootState) => state.institution)
  const isAccountDetailsPage = pathname.includes('account-details')
  // Check if we're on the account details path AND not showing external content
  const isAccountDetailsActive = isAccountDetailsPage && contentUrl === ''

  const keyboardControls = useKeyboardControls()
  const bgMusic = useBackgroundMusic()

  const bgmTrackNormal = getBgmUrl(BgmTrack.Dashboard)
  const bgmTrackCompleted = getBgmUrl(BgmTrack.Completed)

  const urlFeatureFlagTrainEnabled = hasUrlFeatureFlagTrain()

  // Manage BG music
  useEffect(() => {
    let url: string
    if (currentAttempt?.status === 'completed') {
      url = bgmTrackCompleted
    } else {
      url = bgmTrackNormal
    }

    bgMusic.play(url)
    return () => {
      bgMusic.pause()
    }
  }, [bgMusic, bgmTrackNormal, bgmTrackCompleted, currentAttempt?.status])

  useEffect(() => {
    if (isAccountDetailsPage) {
      setCurrentPageTitle({
        title: accountDetailsTitle,
        addScreenReaderH1: false // Don't add an H1 for account details as it already has one
      })
    }
  }, [isAccountDetailsPage])

  useEffect(() => {
    if (pageType !== '' && !isAccountDetailsPage) {
      navigate('/user-dashboard/setting/account-details')
    }

    if (institution === null) {
      apiServices.getAccount()
        .then((res): void => {
          dispatch(setInstitution(res.data.account))
        })
        .catch(err => console.error(err))
    }
  }, [dispatch, institution, navigate, pageType, pathname, isAccountDetailsPage])

  const fetchCurrentAttempt = useCallback(async () => {
    if (currentAttempt == null) {
      const currentAttempt = (await apiServices.getCurrentAttemptAndSession())?.data?.data?.attempt
      dispatch(setCurrentAttempt(currentAttempt))
    }
  }, [dispatch, currentAttempt])

  useEffect(() => {
    void fetchCurrentAttempt()
  }, [fetchCurrentAttempt])

  const fetchContent = (sidebarItem: SideBarItem): void => {
    setContentUrl(
      sidebarItem.slot === CAMPUS_SLUG
        ? institution?.campusPolicyUrl !== undefined ? institution.campusPolicyUrl : ''
        : sidebarItem.webPageUrl
    )
    setCurrentPageTitle({
      title: sidebarItem.title,
      addScreenReaderH1: true // External content needs an H1
    })
  }

  useEffect(() => {
    if (institution !== null) {
      let filteredLinks = []
      if (institution.campusPolicyUrl === undefined) {
        filteredLinks = sidebarItems.filter(item => item.slot !== CAMPUS_SLUG)
      } else {
        filteredLinks = sidebarItems
      }

      if (urlFeatureFlagTrainEnabled) {
        filteredLinks = sidebarItems.filter(item => item.title !== SidebarItemTitle.YourPolicies)
      }

      setSidebarLinks(filteredLinks)
    }
  }, [institution, urlFeatureFlagTrainEnabled])

  const reDownloadCertificate = useCallback(async () => {
    if (currentAttempt?.status === 'completed') {
      void downloadCertificate(userProfile, institution, currentAttempt)
    }
  }, [currentAttempt, institution, userProfile])

  const renderExternalContent = (): JSX.Element => {
    if (contentUrl === '') {
      return (
        <Outlet />
      )
    }

    return (
      <KeyboardControl isExternal>
        <IFrame
          id={FocusableElement.IframeExternalContent}
          src={contentUrl}
          onLoad={() => keyboardControls.setFocusedElement(FocusableElement.IframeExternalContent)}
          style={{ width: '100%', height: '100%' }}
        />
      </KeyboardControl>
    )
  }

  const handleAccountDetails = (): void => {
    navigate('account-details')
    setContentUrl('')
    setCurrentPageTitle({
      title: accountDetailsTitle,
      addScreenReaderH1: false // Don't add an H1 for account details as it already has one
    })
  }

  const handleSignOut = (): void => {
    signout()
      .then(() => {
        setUserProfile(null)
        navigate('/')
      })
      .catch((error) => console.error(error))
  }

  return (
    <div className='font-primary flex bg-[#030303] min-h-screen p-8'>
      <SkipLink targetId='main-content' />

      {/* This is just a visual container that appears to hold the sidebar */}
      <div className='flex flex-col glow-effect pointer-events-none'>
        <div className='flex flex-col h-full relative min-w-[325px] shadow-[0px_0px_20px_-4px_rgba(52,232,158,1)] userdashboard-corner bg-dopage-lightGreen p-[5px]'>
          <div className='inside bg-neutral-2c h-full w-full flex flex-col relative min-w-[325px]'>
            <header role='banner' className='flex justify-center mt-[38px]'>
              <img
                id={FocusableElement.ImageLogo}
                src={logo}
                className='w-[138px] cursor-pointer'
                alt='DopaGE Logo'
              />
            </header>
          </div>
        </div>
      </div>

      <div className='flex flex-col w-full ml-4'>

        <main id='main-content' role='main' tabIndex={-1} className='flex w-full relative h-full'>
          {!isAccountDetailsActive && (
            <PageTitle
              title={currentPageTitle.title}
              addScreenReaderH1={currentPageTitle.addScreenReaderH1}
            />
          )}

          <div className='relative w-full flex'>
            <div className='absolute top-0 left-[-350px] w-[325px] h-full z-10 pointer-events-auto flex flex-col justify-between'>
              <div className='flex flex-col'>

                <nav className='flex flex-col mt-[190px]'>
                  <div className='px-[24px]'>
                    <ReturnButton
                      id='return-button-settings'
                      onClick={() => navigate('/user-dashboard')}
                      text='Return to Dashboard'
                    />
                  </div>

                  <ul className='px-[24px] mt-[96px] flex flex-col'>
                    {sidebarLinks.map((link, key) => {
                      const id = `side-bar-menu-${key}`
                      return (
                        <ControlHighlight
                          key={id}
                          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(id)}
                          highlightEffect={HighlightEffect.ButtonHexOutline}
                        >
                          <KeyboardControl>
                            <button
                              id={id}
                              className={`flex cursor-pointer items-center text-[18px] rounded text-dopage-lightGreen font-bold py-[14px] px-[17px] hover:bg-[rgba(52,232,158,0.2)] ${(pageType === link.slot) ? 'bg-[rgba(52,232,158,0.2)]' : ''}`}
                              onClick={() => fetchContent(link)}
                              aria-label={getAriaLabel(link.title)}
                            >
                              <img
                                src={link.icon}
                                className='w-[27px]'
                                alt=''
                                aria-hidden='true'
                              />
                              <span className='ml-4'>{link.title}</span>
                            </button>
                          </KeyboardControl>
                        </ControlHighlight>
                      )
                    })}

                    {currentAttempt?.status === 'completed' &&
                      <ControlHighlight
                        key={idSidebarLinkCertificate}
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idSidebarLinkCertificate)}
                        highlightEffect={HighlightEffect.ButtonHexOutline}
                      >
                        <KeyboardControl>
                          <button
                            id={idSidebarLinkCertificate}
                            className='flex cursor-pointer items-center text-[18px] rounded text-dopage-lightGreen font-bold py-[14px] px-[17px] hover:bg-[rgba(52,232,158,0.2)]'
                            onClick={() => { void reDownloadCertificate() }}
                            aria-label='Download certificate'
                          >
                            <img
                              src={downloadIcon}
                              className='w-[27px]'
                              alt=''
                              aria-hidden='true'
                            />
                            <span className='ml-4'>Download Certificate</span>
                          </button>
                        </KeyboardControl>
                      </ControlHighlight>}

                  </ul>
                </nav>
              </div>

              <div className='px-[24px] mt-auto mb-[50px] flex flex-col gap-[8px]'>
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonAccountDetails)}
                  highlightEffect={HighlightEffect.ButtonHexOutline}
                  data-testid={`highlight-${FocusableElement.ButtonAccountDetails}`}
                >
                  <KeyboardControl>
                    <button
                      id={FocusableElement.ButtonAccountDetails}
                      data-testid={FocusableElement.ButtonAccountDetails}
                      className={`px-[17px] flex cursor-pointer items-center text-[18px] rounded text-dopage-lightGreen font-bold py-[14px] hover:bg-[rgba(52,232,158,0.2)] ${isAccountDetailsPage ? 'bg-[rgba(52,232,158,0.2)]' : ''}`}
                      onClick={handleAccountDetails}
                      aria-label='Account Details'
                    >
                      <img src={menuIcon} className='w-[27px]' alt='' aria-hidden='true' />
                      <span className='ml-4'>Account details</span>
                    </button>
                  </KeyboardControl>
                </ControlHighlight>

                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonLogout)}
                  highlightEffect={HighlightEffect.ButtonHexOutline}
                  data-testid={`highlight-${FocusableElement.ButtonLogout}`}
                >
                  <KeyboardControl>
                    <button
                      id={FocusableElement.ButtonLogout}
                      data-testid='highlight-button-logout'
                      className='px-[17px] flex cursor-pointer items-center text-[18px] rounded text-dopage-lightGreen font-bold py-[14px] hover:bg-[rgba(52,232,158,0.2)]'
                      onClick={handleSignOut}
                      aria-label='Log Out'
                    >
                      <ProgressiveImage src={logoutIcon} alt='' className='w-[27px]' />
                      <Text
                        type='span'
                        text='LOG OUT'
                        size='SM'
                        bold
                        className='ml-4 text-dopage-lightGreen'
                      />
                    </button>
                  </KeyboardControl>
                </ControlHighlight>
              </div>
            </div>

            <ControlHighlight
              active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.IframeExternalContent)}
              highlightEffect={HighlightEffect.Iframe}
              className='relative h-full shadow-[0px_0px_20px_-4px_rgba(52,232,158,1)] bg-dopage-lightGreen p-[5px] userdashboard-corner flex-grow'
              activeClassName='!bg-dopage-accessibilityHighlight'
            >
              <div className='inside bg-neutral-2c h-full w-full flex flex-col'>
                <div className='flex-1'>
                  {renderExternalContent()}
                </div>
                <AccountDetailsFooter
                  isVisible={isAccountDetailsActive}
                />
              </div>
            </ControlHighlight>
          </div>
        </main>
      </div>
    </div>
  )
}

export default Dashboard
